import { invalidateCachePartialMatch } from "@sg/query";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { agent } from "~/util/agent";
import { pollUntilProjected, readAggregateId } from "~/util/pollUntilProjected";

const AccountType = z.enum(["Main", "Sub", "Vault"]);

const VenueAccountSchema = z.object({
  id: z.string().uuid(),
  venue_id: z.string().uuid(),
  external_ref: z.string().min(1),
  name: z.string().min(1),
  account_type: AccountType,
  purpose: z.literal("Hedging"),
  enabled: z.boolean(),
});

const CreateVenueAccountSchema = VenueAccountSchema.omit({ id: true });

type VenueAccount = z.infer<typeof VenueAccountSchema>;
type CreateVenueAccountInput = z.infer<typeof CreateVenueAccountSchema>;

const QUERY_KEY = "venue-accounts";

const useVenueAccounts = (venueId?: string) =>
  useQuery(
    [QUERY_KEY, venueId],
    async () => agent(`venue_accounts?venue_id=${venueId}`).json<VenueAccount[]>(),
    { enabled: !!venueId }
  );

const useCreateVenueAccount = () =>
  useMutation(
    async (input: CreateVenueAccountInput) => {
      const envelope = await agent
        .post("venue_accounts", { labels: ["created", "venue account"], json: input })
        .json<{ aggregate_id?: string; id?: string }>();
      const id = readAggregateId(envelope);
      return pollUntilProjected(() => agent(`venue_accounts/${id}`).json<VenueAccount>());
    },
    {
      onSuccess() {
        invalidateCachePartialMatch([QUERY_KEY]);
      },
    }
  );

const useUpdateVenueAccount = () =>
  useMutation(
    async (input: { id: string; enabled: boolean }) => {
      return agent
        .patch(`venue_accounts/${input.id}`, {
          labels: ["updated", "venue account"],
          json: { enabled: input.enabled },
        })
        .json<VenueAccount>();
    },
    {
      onSuccess() {
        invalidateCachePartialMatch([QUERY_KEY]);
      },
    }
  );

export { CreateVenueAccountSchema, useCreateVenueAccount, useUpdateVenueAccount, useVenueAccounts };
export type { CreateVenueAccountInput, VenueAccount };
